// ContentMapPage.tsx — Content Views (Structured mini-app) v2
//
// Two complementary views over the SAME content_plan_items table that
// /content-plan renders as a flat board:
//
//   • Story Map — pillars → story arcs → pieces (3-level progressive
//     disclosure: overview grid → funnel arc → detail).
//   • Calendar — monthly grid of scheduled pieces with image thumbnails,
//     plus a backlog rail for unscheduled items.
//
// Both views open the SAME right-side slide-in detail panel — the shared
// detail pattern across content views. The existing /content-plan flat
// table is unchanged.
//
// New columns on content_plan_items (nullable, Leo/MJ-164 populates):
//   story_arc text, story_order int, scheduled_date date, image_url text
//
// MJ-259: drawer reading order — Description first + bigger, cost last.

import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Film,
  Image as ImageIcon,
  LayoutGrid,
  Lightbulb,
  Loader2,
  X,
} from "lucide-react";
import { useApi } from "@/lib/api";
import { architectureT as T } from "../blueprint/ArchitectureBlocks";
import { DesignSystemView } from "./DesignSystemView";
import { BacklogView } from "./BacklogView";
import { InspirationsView } from "./InspirationsView";
import { ShortsView } from "./ShortsView";

export type Item = {
  id: string;
  title: string;
  category: string | null;
  category_order: number | null;
  content_type: string | null;
  funnel_stage: string | null;
  status: string | null;
  priority: string | null;
  description: string | null;
  sort_order: number | null;
  story_arc: string | null;
  story_order: number | null;
  scheduled_date: string | null;
  image_url: string | null;
  video_url: string | null;
  image_urls: string[] | null;
  production_cost_usd: number | null;
  production_cost_breakdown: string | null;
  psychological_angle: string | null;
  marketing_angle: string | null;
  rationale: string | null;
  created_at: string;
};

type ApiResponse = { items: Item[] };

const FUNNEL_ORDER = ["TOFU", "MOFU", "BOFU"] as const;
type FunnelStage = (typeof FUNNEL_ORDER)[number];

const FUNNEL_COLORS: Record<string, { fg: string; bg: string; border: string }> = {
  TOFU: { fg: T.green, bg: T.greenSoft, border: T.green },
  MOFU: { fg: T.amber, bg: T.amberSoft, border: T.amber },
  BOFU: { fg: T.plum, bg: T.plumSoft, border: T.plum },
};

// One-line summaries per category, curated. Falls back to a generic line.
const PILLAR_SUMMARY: Record<string, string> = {
  "Marketing": "Top-of-funnel attention — posts, demos, and proof that put MyJarvis on the radar.",
  "Sales": "Mid-funnel conversion — playbooks, scripts, and assets that turn interest into commitment.",
  "Operations": "How the team and the product actually run — agents, routines, the dashboard spine.",
  "Case Studies": "Real customers, real outcomes — the evidence that the system works in production.",
  "Pilot to Production": "How a discovery call becomes a deployed agent: scope, pilot, hand-off, scale.",
  "Trust & Governance": "Security, controls, and the answers procurement and legal need before signing.",
  "Educational / Frameworks": "Teach the mental models — how to think about AI agents, ROI, deployment.",
  "Benchmark / Data": "Numbers that anchor the pitch — TCO, performance, productivity, win rates.",
  "Distribution Assets": "Decks, one-pagers, landing pages — the formats partners and prospects ask for.",
};

function pillarSummary(category: string | null): string {
  if (!category) return "Uncategorized content pieces.";
  return PILLAR_SUMMARY[category] ?? `Content under the ${category} pillar.`;
}

// ── Tiny shared chips ────────────────────────────────────────────────────────

function PriorityBadge({ priority }: { priority: string | null }) {
  if (!priority) return null;
  const p = priority.toLowerCase();
  const color =
    p === "high" || p === "p0" || p === "p1"
      ? { fg: T.red, bg: T.redSoft, border: T.red }
      : p === "medium" || p === "p2"
        ? { fg: T.amber, bg: T.amberSoft, border: T.amber }
        : { fg: T.ink2, bg: T.bg2, border: T.line };
  return (
    <span
      style={{
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: color.fg,
        background: color.bg,
        border: `1px solid ${color.border}`,
        padding: "2px 7px",
        borderRadius: 999,
        whiteSpace: "nowrap",
      }}
    >
      {priority}
    </span>
  );
}

function ContentTypeChip({ type }: { type: string | null }) {
  if (!type) return null;
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        color: T.skyDark,
        background: T.skySoft,
        border: `1px solid ${T.sky}`,
        padding: "2px 8px",
        borderRadius: 6,
        whiteSpace: "nowrap",
      }}
    >
      {type}
    </span>
  );
}

// ── Funnel coverage bar (Story Map L1) ───────────────────────────────────────

function FunnelBar({
  tofu,
  mofu,
  bofu,
  total,
}: {
  tofu: number;
  mofu: number;
  bofu: number;
  total: number;
}) {
  if (total === 0) return null;
  const segs: Array<{ key: FunnelStage; n: number }> = [
    { key: "TOFU", n: tofu },
    { key: "MOFU", n: mofu },
    { key: "BOFU", n: bofu },
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div
        style={{
          display: "flex",
          height: 10,
          borderRadius: 999,
          overflow: "hidden",
          background: T.bg2,
          border: `1px solid ${T.line}`,
        }}
      >
        {segs.map((s) =>
          s.n > 0 ? (
            <div
              key={s.key}
              style={{
                width: `${(s.n / total) * 100}%`,
                background: FUNNEL_COLORS[s.key].fg,
              }}
              title={`${s.key}: ${s.n}`}
            />
          ) : null,
        )}
      </div>
      <div style={{ display: "flex", gap: 12, fontSize: 11, color: T.ink3, fontWeight: 600 }}>
        {segs.map((s) => (
          <span key={s.key} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: FUNNEL_COLORS[s.key].fg,
                opacity: s.n === 0 ? 0.3 : 1,
              }}
            />
            <span style={{ color: s.n === 0 ? T.ink3 : T.ink2 }}>
              {s.key} <strong style={{ color: T.ink }}>{s.n}</strong>
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Pillar card (Story Map L1) ───────────────────────────────────────────────

function PillarCard({
  category,
  items,
  onClick,
}: {
  category: string;
  items: Item[];
  onClick: () => void;
}) {
  const tofu = items.filter((i) => i.funnel_stage === "TOFU").length;
  const mofu = items.filter((i) => i.funnel_stage === "MOFU").length;
  const bofu = items.filter((i) => i.funnel_stage === "BOFU").length;
  const total = items.length;

  return (
    <button
      onClick={onClick}
      style={{
        background: T.white,
        border: `1px solid ${T.line}`,
        borderRadius: 14,
        padding: "22px 22px 20px",
        textAlign: "left",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: 18,
        fontFamily: "Inter, sans-serif",
        transition: "transform 0.15s, box-shadow 0.15s, border-color 0.15s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 14px 36px rgba(16,24,40,0.10)";
        e.currentTarget.style.transform = "translateY(-3px)";
        e.currentTarget.style.borderColor = T.sky;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.transform = "none";
        e.currentTarget.style.borderColor = T.line;
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.1em",
              color: T.skyDark,
              textTransform: "uppercase",
              marginBottom: 6,
            }}
          >
            Pillar
          </div>
          <h3
            style={{
              fontSize: 19,
              fontWeight: 800,
              color: T.ink,
              margin: 0,
              letterSpacing: "-0.01em",
              lineHeight: 1.25,
            }}
          >
            {category}
          </h3>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            lineHeight: 1,
          }}
        >
          <div
            style={{
              fontSize: 32,
              fontWeight: 800,
              color: T.accent,
              letterSpacing: "-0.03em",
            }}
          >
            {total}
          </div>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.08em",
              color: T.ink3,
              textTransform: "uppercase",
              marginTop: 4,
            }}
          >
            {total === 1 ? "piece" : "pieces"}
          </div>
        </div>
      </div>

      <p
        style={{
          fontSize: 13,
          color: T.ink2,
          lineHeight: 1.55,
          margin: 0,
          minHeight: 40,
        }}
      >
        {pillarSummary(category)}
      </p>

      <FunnelBar tofu={tofu} mofu={mofu} bofu={bofu} total={total} />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12,
          fontWeight: 700,
          color: T.accent,
          letterSpacing: "0.02em",
          marginTop: 2,
        }}
      >
        Open story arc
        <ChevronRight style={{ width: 14, height: 14 }} />
      </div>
    </button>
  );
}

// ── Piece tile (Story Map L2) ────────────────────────────────────────────────

function PieceTile({ item, onClick }: { item: Item; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: T.white,
        border: `1px solid ${T.line}`,
        borderRadius: 10,
        padding: "13px 14px",
        textAlign: "left",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: 9,
        fontFamily: "Inter, sans-serif",
        transition: "transform 0.12s, box-shadow 0.12s, border-color 0.12s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 8px 22px rgba(16,24,40,0.08)";
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.borderColor = T.sky;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.transform = "none";
        e.currentTarget.style.borderColor = T.line;
      }}
    >
      <div
        style={{
          fontSize: 13.5,
          fontWeight: 700,
          color: T.ink,
          lineHeight: 1.35,
          letterSpacing: "-0.005em",
        }}
      >
        {item.title}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 5, alignItems: "center" }}>
        <ContentTypeChip type={item.content_type} />
        <PriorityBadge priority={item.priority} />
      </div>
    </button>
  );
}

// ── Funnel column (Story Map L2) ─────────────────────────────────────────────

function FunnelColumn({
  stage,
  items,
  onPick,
}: {
  stage: FunnelStage;
  items: Item[];
  onPick: (item: Item) => void;
}) {
  const c = FUNNEL_COLORS[stage];
  const subtitleMap: Record<FunnelStage, string> = {
    TOFU: "Awareness · Hook",
    MOFU: "Evaluation · Trust",
    BOFU: "Decision · Proof",
  };

  return (
    <div
      style={{
        background: T.white,
        border: `1px solid ${T.line}`,
        borderTop: `3px solid ${c.fg}`,
        borderRadius: 12,
        padding: "16px 14px 14px",
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 14,
              fontWeight: 800,
              color: c.fg,
              letterSpacing: "0.04em",
            }}
          >
            {stage}
          </div>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.08em",
              color: T.ink3,
              textTransform: "uppercase",
              marginTop: 2,
            }}
          >
            {subtitleMap[stage]}
          </div>
        </div>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: T.ink3,
            background: T.bg2,
            border: `1px solid ${T.line}`,
            padding: "2px 8px",
            borderRadius: 999,
          }}
        >
          {items.length}
        </span>
      </div>

      {items.length === 0 ? (
        <div
          style={{
            padding: "16px 8px",
            fontSize: 12,
            color: T.ink3,
            textAlign: "center",
            border: `1px dashed ${T.line}`,
            borderRadius: 8,
            background: T.bg,
          }}
        >
          No pieces yet
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {items.map((item) => (
            <PieceTile key={item.id} item={item} onClick={() => onPick(item)} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Calendar view ────────────────────────────────────────────────────────────

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DOW_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function ymd(date: Date): string {
  // Local YYYY-MM-DD — Postgres `date` round-trips as a string, so we match
  // its format here instead of using ISO (which would shift via UTC).
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function CalendarTile({ item, onClick }: { item: Item; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      title={item.title}
      style={{
        background: T.white,
        border: `1px solid ${T.line}`,
        borderRadius: 8,
        padding: 0,
        textAlign: "left",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        fontFamily: "Inter, sans-serif",
        transition: "transform 0.12s, box-shadow 0.12s, border-color 0.12s",
        width: "100%",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 6px 18px rgba(16,24,40,0.10)";
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.borderColor = T.sky;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.transform = "none";
        e.currentTarget.style.borderColor = T.line;
      }}
    >
      {item.image_url ? (
        <div
          style={{
            width: "100%",
            aspectRatio: "16 / 9",
            background: `${T.bg2} center/cover no-repeat url(${JSON.stringify(item.image_url)})`,
          }}
        />
      ) : (
        <div
          style={{
            width: "100%",
            aspectRatio: "16 / 9",
            background: T.bg2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: T.ink3,
          }}
        >
          <ImageIcon style={{ width: 18, height: 18 }} />
        </div>
      )}
      <div style={{ padding: "8px 9px", display: "flex", flexDirection: "column", gap: 5 }}>
        <div
          style={{
            fontSize: 11.5,
            fontWeight: 700,
            color: T.ink,
            lineHeight: 1.25,
            letterSpacing: "-0.005em",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {item.title}
        </div>
        {item.content_type && (
          <div style={{ display: "flex" }}>
            <span
              style={{
                fontSize: 8.5,
                fontWeight: 700,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: T.skyDark,
                background: T.skySoft,
                border: `1px solid ${T.sky}`,
                padding: "1px 5px",
                borderRadius: 4,
                whiteSpace: "nowrap",
              }}
            >
              {item.content_type}
            </span>
          </div>
        )}
      </div>
    </button>
  );
}

function BacklogTile({ item, onClick }: { item: Item; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: T.white,
        border: `1px dashed ${T.line}`,
        borderRadius: 8,
        padding: "10px 12px",
        textAlign: "left",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontFamily: "Inter, sans-serif",
        transition: "border-color 0.12s, background 0.12s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = T.sky;
        e.currentTarget.style.background = T.bg;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = T.line;
        e.currentTarget.style.background = T.white;
      }}
    >
      <div
        style={{
          flexShrink: 0,
          width: 40,
          height: 40,
          borderRadius: 6,
          background: item.image_url
            ? `${T.bg2} center/cover no-repeat url(${JSON.stringify(item.image_url)})`
            : T.bg2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: T.ink3,
        }}
      >
        {!item.image_url && <ImageIcon style={{ width: 14, height: 14 }} />}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: 12.5,
            fontWeight: 700,
            color: T.ink,
            lineHeight: 1.3,
            letterSpacing: "-0.005em",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {item.title}
        </div>
        <div
          style={{
            fontSize: 10,
            color: T.ink3,
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            marginTop: 2,
          }}
        >
          {item.category ?? "Uncategorized"}
          {item.content_type ? ` · ${item.content_type}` : ""}
        </div>
      </div>
    </button>
  );
}

function CalendarView({
  items,
  onPick,
}: {
  items: Item[];
  onPick: (item: Item) => void;
}) {
  const today = new Date();
  const [anchor, setAnchor] = useState<{ year: number; month: number }>({
    year: today.getFullYear(),
    month: today.getMonth(),
  });

  const { weeks, scheduledByDay, backlog } = useMemo(() => {
    const scheduledByDay = new Map<string, Item[]>();
    const backlog: Item[] = [];
    for (const i of items) {
      if (i.scheduled_date) {
        const k = i.scheduled_date.slice(0, 10);
        const list = scheduledByDay.get(k) ?? [];
        list.push(i);
        scheduledByDay.set(k, list);
      } else {
        backlog.push(i);
      }
    }
    backlog.sort((a, b) => {
      const ao = a.category_order ?? 999;
      const bo = b.category_order ?? 999;
      if (ao !== bo) return ao - bo;
      const aso = a.sort_order ?? 999;
      const bso = b.sort_order ?? 999;
      if (aso !== bso) return aso - bso;
      return a.title.localeCompare(b.title);
    });

    const first = new Date(anchor.year, anchor.month, 1);
    const startDow = first.getDay();
    const gridStart = new Date(anchor.year, anchor.month, 1 - startDow);
    const weeks: Array<Array<{ date: Date; inMonth: boolean; key: string }>> = [];
    let cursor = new Date(gridStart);
    for (let w = 0; w < 6; w++) {
      const row: Array<{ date: Date; inMonth: boolean; key: string }> = [];
      for (let d = 0; d < 7; d++) {
        row.push({
          date: new Date(cursor),
          inMonth: cursor.getMonth() === anchor.month,
          key: ymd(cursor),
        });
        cursor.setDate(cursor.getDate() + 1);
      }
      weeks.push(row);
    }
    return { weeks, scheduledByDay, backlog };
  }, [items, anchor]);

  const goPrev = () => {
    setAnchor((a) => {
      const m = a.month - 1;
      return m < 0 ? { year: a.year - 1, month: 11 } : { year: a.year, month: m };
    });
  };
  const goNext = () => {
    setAnchor((a) => {
      const m = a.month + 1;
      return m > 11 ? { year: a.year + 1, month: 0 } : { year: a.year, month: m };
    });
  };
  const goToday = () => {
    const t = new Date();
    setAnchor({ year: t.getFullYear(), month: t.getMonth() });
  };

  const todayKey = ymd(today);
  const scheduledThisMonth = weeks
    .flat()
    .filter((c) => c.inMonth)
    .reduce((n, c) => n + (scheduledByDay.get(c.key)?.length ?? 0), 0);

  return (
    <>
      <div style={{ marginBottom: 24 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.1em",
            color: T.skyDark,
            textTransform: "uppercase",
            marginBottom: 12,
          }}
        >
          MyJarvis · Marketing
        </div>
        <h1
          style={{
            fontSize: 36,
            fontWeight: 800,
            color: T.ink,
            margin: "0 0 14px",
            letterSpacing: "-0.02em",
            lineHeight: 1.1,
          }}
        >
          Content Calendar
        </h1>
        <p
          style={{
            fontSize: 16.5,
            color: T.ink2,
            lineHeight: 1.6,
            maxWidth: 820,
            margin: 0,
          }}
        >
          Monthly publishing schedule. Scheduled pieces appear on their day; unscheduled
          drafts sit in the backlog below. Leo (MJ-164) keeps the dates fresh.
        </p>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 14,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <h2
            style={{
              fontSize: 22,
              fontWeight: 800,
              color: T.ink,
              margin: 0,
              letterSpacing: "-0.01em",
            }}
          >
            {MONTH_NAMES[anchor.month]} {anchor.year}
          </h2>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: T.ink3,
              background: T.bg2,
              border: `1px solid ${T.line}`,
              padding: "2px 9px",
              borderRadius: 999,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            {scheduledThisMonth} scheduled
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            onClick={goPrev}
            aria-label="Previous month"
            style={{
              background: T.white,
              border: `1px solid ${T.line}`,
              borderRadius: 8,
              padding: 6,
              cursor: "pointer",
              color: T.ink2,
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            <ChevronLeft style={{ width: 16, height: 16 }} />
          </button>
          <button
            onClick={goToday}
            style={{
              background: T.white,
              border: `1px solid ${T.line}`,
              borderRadius: 8,
              padding: "6px 12px",
              cursor: "pointer",
              fontFamily: "Inter, sans-serif",
              fontSize: 12,
              fontWeight: 700,
              color: T.accent,
              letterSpacing: "0.02em",
            }}
          >
            Today
          </button>
          <button
            onClick={goNext}
            aria-label="Next month"
            style={{
              background: T.white,
              border: `1px solid ${T.line}`,
              borderRadius: 8,
              padding: 6,
              cursor: "pointer",
              color: T.ink2,
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            <ChevronRight style={{ width: 16, height: 16 }} />
          </button>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          gap: 0,
          background: T.white,
          border: `1px solid ${T.line}`,
          borderRadius: "12px 12px 0 0",
          borderBottom: "none",
        }}
      >
        {DOW_LABELS.map((d) => (
          <div
            key={d}
            style={{
              padding: "10px 12px",
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "0.1em",
              color: T.ink3,
              textTransform: "uppercase",
              borderRight: `1px solid ${T.line}`,
              background: T.bg,
            }}
          >
            {d}
          </div>
        ))}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          gridAutoRows: "minmax(132px, auto)",
          background: T.white,
          border: `1px solid ${T.line}`,
          borderRadius: "0 0 12px 12px",
        }}
      >
        {weeks.flat().map((cell, idx) => {
          const dayItems = scheduledByDay.get(cell.key) ?? [];
          const isToday = cell.key === todayKey;
          const isFirstCol = idx % 7 === 0;
          const isLastRow = idx >= 35;
          return (
            <div
              key={cell.key + idx}
              style={{
                borderRight: idx % 7 < 6 ? `1px solid ${T.line}` : "none",
                borderTop: idx >= 7 ? `1px solid ${T.line}` : "none",
                padding: "8px 8px 10px",
                display: "flex",
                flexDirection: "column",
                gap: 6,
                background: cell.inMonth ? T.white : T.bg,
                opacity: cell.inMonth ? 1 : 0.55,
                minWidth: 0,
                borderBottomLeftRadius: isFirstCol && isLastRow ? 12 : 0,
                borderBottomRightRadius: idx === 41 ? 12 : 0,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 4,
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: isToday ? T.white : cell.inMonth ? T.ink : T.ink3,
                    background: isToday ? T.accent : "transparent",
                    borderRadius: 999,
                    padding: isToday ? "2px 8px" : 0,
                    letterSpacing: "-0.005em",
                  }}
                >
                  {cell.date.getDate()}
                </span>
                {dayItems.length > 0 && (
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: 700,
                      color: T.ink3,
                      letterSpacing: "0.06em",
                    }}
                  >
                    {dayItems.length}
                  </span>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 5, minWidth: 0 }}>
                {dayItems.slice(0, 2).map((it) => (
                  <CalendarTile key={it.id} item={it} onClick={() => onPick(it)} />
                ))}
                {dayItems.length > 2 && (
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: T.ink3,
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      padding: "2px 4px",
                    }}
                  >
                    +{dayItems.length - 2} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

    </>
  );
}

// ── Detail drawer (shared across views — right-side slide-in panel) ─────────

function DetailField({
  label,
  body,
  large = false,
}: {
  label: string;
  body: string | null;
  large?: boolean;
}) {
  if (!body?.trim()) return null;
  return (
    <div>
      <div
        style={{
          fontSize: large ? 11 : 10,
          fontWeight: 700,
          letterSpacing: "0.1em",
          color: T.skyDark,
          textTransform: "uppercase",
          marginBottom: large ? 10 : 6,
        }}
      >
        {label}
      </div>
      <p
        style={{
          fontSize: large ? 16.5 : 13.5,
          color: T.ink,
          lineHeight: large ? 1.7 : 1.6,
          margin: 0,
          whiteSpace: "pre-wrap",
        }}
      >
        {body}
      </p>
    </div>
  );
}

function DetailDrawer({ item, onClose }: { item: Item; onClose: () => void }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        display: "flex",
        justifyContent: "flex-end",
      }}
    >
      <div
        onClick={onClose}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(15,23,42,0.45)",
        }}
      />

      <aside
        style={{
          position: "relative",
          width: "min(560px, 92vw)",
          height: "100%",
          background: T.white,
          borderLeft: `1px solid ${T.line}`,
          boxShadow: "-24px 0 48px rgba(16,24,40,0.18)",
          overflowY: "auto",
          fontFamily: "Inter, sans-serif",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 22px",
            borderBottom: `1px solid ${T.line}`,
            background: T.bg,
            position: "sticky",
            top: 0,
            zIndex: 1,
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.1em",
              color: T.skyDark,
              textTransform: "uppercase",
            }}
          >
            {item.category ?? "Detail"}
          </div>
          <button
            onClick={onClose}
            aria-label="Close detail"
            style={{
              background: T.white,
              border: `1px solid ${T.line}`,
              borderRadius: 8,
              padding: 6,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              color: T.ink2,
            }}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        <div
          style={{
            padding: "22px 26px 60px",
            display: "flex",
            flexDirection: "column",
            gap: 22,
          }}
        >
          {item.video_url && (
            <video
              controls
              playsInline
              preload="metadata"
              src={item.video_url}
              style={{
                width: "100%",
                borderRadius: 10,
                border: `1px solid ${T.line}`,
                background: T.bg2,
                display: "block",
              }}
            />
          )}

          {item.image_urls && item.image_urls.length > 0 ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 10,
              }}
            >
              {item.image_urls.map((url, i) => (
                <a
                  key={url}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: "block" }}
                >
                  <img
                    src={url}
                    alt={`Slide ${i + 1}`}
                    loading="lazy"
                    style={{
                      width: "100%",
                      aspectRatio: "1 / 1",
                      objectFit: "cover",
                      borderRadius: 10,
                      border: `1px solid ${T.line}`,
                      background: T.bg2,
                      display: "block",
                    }}
                  />
                </a>
              ))}
            </div>
          ) : (
            item.image_url && (
              <div
                style={{
                  width: "100%",
                  aspectRatio: "16 / 9",
                  borderRadius: 10,
                  background: `${T.bg2} center/cover no-repeat url(${JSON.stringify(item.image_url)})`,
                  border: `1px solid ${T.line}`,
                }}
              />
            )
          )}

          <h2
            style={{
              fontSize: 24,
              fontWeight: 800,
              color: T.ink,
              margin: 0,
              letterSpacing: "-0.015em",
              lineHeight: 1.25,
            }}
          >
            {item.title}
          </h2>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            <ContentTypeChip type={item.content_type} />
            {item.funnel_stage && (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: FUNNEL_COLORS[item.funnel_stage]?.fg ?? T.ink2,
                  background: FUNNEL_COLORS[item.funnel_stage]?.bg ?? T.bg2,
                  border: `1px solid ${FUNNEL_COLORS[item.funnel_stage]?.border ?? T.line}`,
                  padding: "2px 9px",
                  borderRadius: 999,
                }}
              >
                {item.funnel_stage}
              </span>
            )}
            <PriorityBadge priority={item.priority} />
            {item.status && (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: T.ink2,
                  background: T.bg2,
                  border: `1px solid ${T.line}`,
                  padding: "2px 9px",
                  borderRadius: 999,
                }}
              >
                {item.status}
              </span>
            )}
            {item.scheduled_date && (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: T.accent,
                  background: T.skySoft,
                  border: `1px solid ${T.sky}`,
                  padding: "2px 9px",
                  borderRadius: 999,
                }}
              >
                {item.scheduled_date}
              </span>
            )}
          </div>

          {/* Description FIRST + BIGGER — the primary reading block of the
              drawer; it now carries the editorial brief (lede + THE DECK +
              WHY, PSYCHOLOGICALLY + FOR OUR ICP) as plain text. */}
          <DetailField label="Description" body={item.description} large />
          <DetailField label="Psychological angle" body={item.psychological_angle} />
          <DetailField label="Marketing angle" body={item.marketing_angle} />
          <DetailField label="Rationale" body={item.rationale} />

          {/* Production cost LAST — bottom of the drawer. */}
          {item.production_cost_usd !== null && (
            <DetailField
              label="Production cost"
              body={`$${Number(item.production_cost_usd).toFixed(2)}${
                item.production_cost_breakdown
                  ? ` — ${item.production_cost_breakdown}`
                  : ""
              }`}
            />
          )}
        </div>
      </aside>
    </div>
  );
}

// ── Story Map view (extracted) ───────────────────────────────────────────────

type StoryMapState =
  | { kind: "overview" }
  | { kind: "pillar"; category: string };

function StoryMapView({
  items,
  onPick,
}: {
  items: Item[];
  onPick: (item: Item) => void;
}) {
  const [view, setView] = useState<StoryMapState>({ kind: "overview" });

  const pillars = useMemo(() => {
    const groups = new Map<string, { order: number; items: Item[] }>();
    for (const i of items) {
      const key = i.category ?? "Uncategorized";
      const g = groups.get(key) ?? { order: i.category_order ?? 999, items: [] };
      g.items.push(i);
      groups.set(key, g);
    }
    return [...groups.entries()]
      .sort((a, b) => a[1].order - b[1].order)
      .map(([category, g]) => ({ category, items: g.items }));
  }, [items]);

  const arc = useMemo(() => {
    if (view.kind !== "pillar") return null;
    const list = items.filter((i) => (i.category ?? "Uncategorized") === view.category);
    const cols: Record<FunnelStage, Item[]> = { TOFU: [], MOFU: [], BOFU: [] };
    const orphans: Item[] = [];
    for (const i of list) {
      if (i.funnel_stage && FUNNEL_ORDER.includes(i.funnel_stage as FunnelStage)) {
        cols[i.funnel_stage as FunnelStage].push(i);
      } else {
        orphans.push(i);
      }
    }
    const sortKey = (a: Item, b: Item) => {
      const ao = a.story_order ?? a.sort_order ?? 999;
      const bo = b.story_order ?? b.sort_order ?? 999;
      return ao - bo;
    };
    cols.TOFU.sort(sortKey);
    cols.MOFU.sort(sortKey);
    cols.BOFU.sort(sortKey);
    orphans.sort(sortKey);
    return { cols, orphans, total: list.length };
  }, [items, view]);

  if (view.kind === "overview") {
    return (
      <>
        <div style={{ marginBottom: 32 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.1em",
              color: T.skyDark,
              textTransform: "uppercase",
              marginBottom: 12,
            }}
          >
            MyJarvis · Marketing
          </div>
          <h1
            style={{
              fontSize: 36,
              fontWeight: 800,
              color: T.ink,
              margin: "0 0 14px",
              letterSpacing: "-0.02em",
              lineHeight: 1.1,
            }}
          >
            Content Story Map
          </h1>
          <p
            style={{
              fontSize: 16.5,
              color: T.ink2,
              lineHeight: 1.6,
              maxWidth: 820,
              margin: 0,
            }}
          >
            The whole content strategy at a glance — pillars on top, story arcs underneath, full
            detail one click deeper. The flat list still lives at{" "}
            <a
              href="/content-plan"
              style={{ color: T.accent, fontWeight: 600, textDecoration: "none" }}
            >
              /content-plan
            </a>
            .
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
            gap: 20,
          }}
        >
          {pillars.map((p) => (
            <PillarCard
              key={p.category}
              category={p.category}
              items={p.items}
              onClick={() => setView({ kind: "pillar", category: p.category })}
            />
          ))}
        </div>
      </>
    );
  }

  if (!arc) return null;
  return (
    <>
      <div style={{ marginBottom: 32 }}>
        <button
          onClick={() => setView({ kind: "overview" })}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: "transparent",
            border: "none",
            padding: 0,
            cursor: "pointer",
            color: T.accent,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            marginBottom: 16,
          }}
        >
          <ArrowLeft style={{ width: 14, height: 14 }} />
          All pillars
        </button>

        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.1em",
            color: T.skyDark,
            textTransform: "uppercase",
            marginBottom: 10,
          }}
        >
          Pillar · Story Arc
        </div>
        <h1
          style={{
            fontSize: 32,
            fontWeight: 800,
            color: T.ink,
            margin: "0 0 12px",
            letterSpacing: "-0.02em",
            lineHeight: 1.15,
          }}
        >
          {view.category}
        </h1>
        <p
          style={{
            fontSize: 15.5,
            color: T.ink2,
            lineHeight: 1.6,
            maxWidth: 780,
            margin: 0,
          }}
        >
          {pillarSummary(view.category)} {arc.total} pieces · read left → right as the funnel
          narrative: problem → mechanism → proof → decision.
        </p>
      </div>

      <div style={{ position: "relative", marginBottom: 12 }}>
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: 28,
            left: "16%",
            right: "16%",
            height: 2,
            background: `linear-gradient(to right, ${T.green}, ${T.amber}, ${T.plum})`,
            opacity: 0.35,
            zIndex: 0,
          }}
        />
        <div
          style={{
            position: "relative",
            zIndex: 1,
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: 18,
          }}
        >
          {FUNNEL_ORDER.map((stage) => (
            <FunnelColumn
              key={stage}
              stage={stage}
              items={arc.cols[stage]}
              onPick={onPick}
            />
          ))}
        </div>
      </div>

      {arc.orphans.length > 0 && (
        <section style={{ marginTop: 36 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.08em",
              color: T.ink3,
              textTransform: "uppercase",
              marginBottom: 12,
            }}
          >
            Unstaged
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
              gap: 12,
            }}
          >
            {arc.orphans.map((item) => (
              <PieceTile key={item.id} item={item} onClick={() => onPick(item)} />
            ))}
          </div>
        </section>
      )}
    </>
  );
}

// ── Page wrapper — tabs + shared drawer ─────────────────────────────────────

type ViewKind = "backlog" | "calendar" | "design" | "inspirations" | "shorts";

function ViewTab({
  active,
  label,
  icon: Icon,
  onClick,
}: {
  active: boolean;
  label: string;
  icon: typeof CalendarIcon;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        background: active ? T.white : "transparent",
        border: `1px solid ${active ? T.line : "transparent"}`,
        boxShadow: active ? "0 1px 2px rgba(16,24,40,0.05)" : "none",
        borderRadius: 8,
        padding: "8px 14px",
        cursor: "pointer",
        fontFamily: "Inter, sans-serif",
        fontSize: 13,
        fontWeight: 700,
        color: active ? T.accent : T.ink2,
        letterSpacing: "0.01em",
        transition: "background 0.15s, color 0.15s, border-color 0.15s",
      }}
    >
      <Icon style={{ width: 15, height: 15 }} />
      {label}
    </button>
  );
}

export function ContentMapPage() {
  const api = useApi();
  const [data, setData] = useState<ApiResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<ViewKind>("backlog");
  const [drawerItem, setDrawerItem] = useState<Item | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api("/api/content-map");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as ApiResponse;
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load");
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const items = data?.items ?? [];

  const shell = (children: React.ReactNode) => (
    <div
      style={{
        fontFamily: "Inter, sans-serif",
        boxSizing: "border-box",
        padding: "32px 48px 80px",
      }}
    >
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        {/* View toggle */}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            background: T.bg2,
            border: `1px solid ${T.line}`,
            borderRadius: 10,
            padding: 4,
            marginBottom: 28,
          }}
        >
          <ViewTab
            active={tab === "backlog"}
            label="Backlog"
            icon={LayoutGrid}
            onClick={() => setTab("backlog")}
          />
          <ViewTab
            active={tab === "calendar"}
            label="Calendar"
            icon={CalendarIcon}
            onClick={() => setTab("calendar")}
          />
          <ViewTab
            active={tab === "design"}
            label="Design System"
            icon={ImageIcon}
            onClick={() => setTab("design")}
          />
          <ViewTab
            active={tab === "inspirations"}
            label="Inspirations"
            icon={Lightbulb}
            onClick={() => setTab("inspirations")}
          />
          <ViewTab
            active={tab === "shorts"}
            label="Shorts"
            icon={Film}
            onClick={() => setTab("shorts")}
          />
        </div>
        {children}
      </div>
      {drawerItem && (
        <DetailDrawer item={drawerItem} onClose={() => setDrawerItem(null)} />
      )}
    </div>
  );

  if (error) {
    return shell(
      <div
        style={{
          padding: "16px 20px",
          color: T.red,
          background: T.redSoft,
          border: `1px solid ${T.red}`,
          borderRadius: 8,
          fontSize: 13,
        }}
      >
        Failed to load content map: {error}
      </div>,
    );
  }

  if (data === null) {
    return shell(
      <div
        style={{
          padding: 24,
          fontSize: 14,
          color: T.ink3,
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} />
        Loading…
      </div>,
    );
  }

  return shell(
    tab === "backlog" ? (
      <BacklogView items={items} onPick={setDrawerItem} />
    ) : tab === "calendar" ? (
      <CalendarView items={items} onPick={setDrawerItem} />
    ) : tab === "design" ? (
      <DesignSystemView />
    ) : tab === "shorts" ? (
      <ShortsView />
    ) : (
      <InspirationsView />
    ),
  );
}

(ContentMapPage as unknown as { path: string }).path = "/content-map";
